/**
 * @file receipt-item-payload.ts
 * @description Тело строки приёмки из панели «Добавить строку» — чистые
 * правила (phase19.22, W20, W22): пустая партия по умолчанию (дат нет —
 * придуманный срок хуже пустого) и сборка запроса (вид «срок не требуется» —
 * даты не отправляем).
 *
 * phase19.31 (SC51): в режиме кладовщика цену продажи не отправляем вовсе —
 * поле скрыто, и даже случайно оставшееся значение не уходит на сервер.
 */

import type { ReceiptItemLotValue } from "./ReceiptItemLotFields";
import type { ExpiryPolicy } from "../../lib/receipt-expiry";

/** Тело `POST /admin/wms/receipts/:id/items`. */
export interface ReceiptItemPayload {
  skuId: string;
  quantity: string;
  unitCost?: string;
  salePrice?: string;
  batchNumber?: string;
  manufacturedAt?: string;
  expiresAt?: string;
  /** phase19.14 (W14) — ячейка размещения; null/undefined — по умолчанию SKU. */
  binId?: string | null;
}

/** Пустая партия; ячейку сбрасывает только смена склада. */
export function emptyLot(binId: string): ReceiptItemLotValue {
  return {
    unitCost: "",
    salePrice: "",
    batchNumber: "",
    binId,
    manufacturedAt: "",
    expiresAt: "",
    expiresManual: false,
  };
}

/** Что отправлять из полей партии. */
export interface ReceiptPayloadOptions {
  /** Цена продажи видна сотруднику; кладовщику — нет (SC51). */
  includeSalePrice?: boolean;
}

/** Запрос строки из состояния панели. */
export function buildReceiptItemPayload(
  skuId: string,
  quantity: string,
  lot: ReceiptItemLotValue,
  policy: ExpiryPolicy,
  { includeSalePrice = true }: ReceiptPayloadOptions = {},
): ReceiptItemPayload {
  const noExpiry = policy.requiresExpiry === false;
  return {
    skuId,
    quantity: quantity.trim(),
    batchNumber: lot.batchNumber.trim() || undefined,
    manufacturedAt: noExpiry ? undefined : lot.manufacturedAt || undefined,
    expiresAt: noExpiry ? undefined : lot.expiresAt || undefined,
    unitCost: lot.unitCost.trim() || undefined,
    salePrice: includeSalePrice ? lot.salePrice.trim() || undefined : undefined,
    binId: lot.binId || null,
  };
}
